/**
 * The behaviour pattern registry — the single declared source of truth for the
 * interactions a crawled template carries, shared by the parser's stamp pass,
 * the runtimes, the render/export surfaces and (from AG-7) the residue gate.
 *
 * Modelled on `shared/motion-patterns.js`, deliberately: that registry stops at
 * animation, and everything below is the half it could never describe — a thing
 * the user CLICKS. Same rules, and they are what make the registry worth having:
 *
 * **PURE DATA — no functions, no imports.** Predicates are enums that
 * `behaviour-contract.js` interprets. A callback here would let the parser, the
 * runtime and the gate drift apart; an enum cannot.
 *
 * **Every row carries `evidence`, and evidence is measured or it says so.** The
 * counts below are from a real census over `data/templates/**` on 2026-09-07
 * (372 sites, 33,415 sections with HTML, jsdom `querySelectorAll` per section).
 * A row with no instance in the corpus says `unmeasured — synthetic fixture` and
 * names the tripwire that will notice when a real one appears. A row with
 * neither is not admissible.
 *
 * **DECLARED is not SHIPPED.** `runtime` names the file in `data/behaviours/`
 * that reconstructs the behaviour, or is `null` for a row that is recognised and
 * not yet reconstructed. Only a row with a runtime may be stamped, so adding a
 * recogniser can never silently change how a site behaves — the AG-2 lesson,
 * where a detector that flipped the segmenter for 21 Astro sites the moment it
 * shipped would have been a corpus rewrite wearing a measurement's clothes.
 *
 * Schema
 *   id            kebab-case, unique, no colons (contract C-4)
 *   support       'runtime' — a runtime reconstructs it
 *                 'static'  — collapsed to a correct resting state on disk
 *                 'none'    — recognised, deliberately not reconstructed
 *   runtime       `<id>.js` under `builder/data/behaviours/`, or null
 *   variants      [{ id, host, exclude, config, stateClasses, controlsScope,
 *                    runtime, evidence }]
 *     id            kebab-case, unique within the entry, no colons
 *     host          selector for the element the stamp goes ON
 *     exclude       selectors that DISQUALIFY a host (a nested widget's root,
 *                   a marker the site uses for something else)
 *     config        the serialized `__pbBehaviour` payload — every selector a
 *                   runtime uses arrives here, because the runtime contract
 *                   forbids selector literals in runtime source
 *     stateClasses  classes the runtime adds/removes. css-prune must keep them:
 *                   they are dormant in the captured HTML by definition, so a
 *                   pruner that keeps only what the markup uses deletes exactly
 *                   the rules the behaviour needs
 *     controlsScope 'host'     — controls live inside the host
 *                   'ancestor' — walk up to the section root to find them (C-8)
 *   legacyGuards  attributes a hand sidecar writes to claim the same host. A
 *                 runtime REFUSES a host carrying one, which is what lets a
 *                 sidecar and a stamp coexist through a release boundary
 *                 without producing two owners of one widget.
 *   sharedWith    (C-3, AG-7) rows in ANOTHER registry that legitimately use the
 *                 same selector literals, as `<module>#<id>`. Selector reuse
 *                 across tables is legal ONLY as a declared derivation or an
 *                 explicit sharing; an undeclared duplicate literal is a hand
 *                 copy and `audit-behaviour-residue.js --check` fails on it.
 *                 The point is not that duplication is forbidden — it is that a
 *                 duplicate has to be a decision somebody wrote down, so that
 *                 the two tables cannot silently drift apart while both look
 *                 authoritative.
 *   evidence      what the corpus showed, per the rule above
 */

export const BEHAVIOUR_PATTERNS = [
  {
    id: 'tabs',
    support: 'runtime',
    runtime: 'tabs.js',
    // The fixup that AG-4 retires writes this to claim `.w-tabs`. Both the
    // sidecar and the stamp are legal through the release; the runtime declines
    // any host wearing the attribute, so exactly one owner binds.
    legacyGuards: ['data-pb-tabs'],
    variants: [
      {
        id: 'webflow',
        host: '.w-tabs',
        exclude: [],
        controlsScope: 'host',
        config: {
          links: '.w-tab-link',
          panes: '.w-tab-pane',
          key: 'data-w-tab',
          linkActive: 'w--current',
          paneActive: 'w--tab-active'
        },
        stateClasses: ['w--current', 'w--tab-active'],
        evidence:
          'fourhorsemenmedia.com, 2 sections (2026-09-07 census: `.w-tabs` = 2 sections / 1 site). ' +
          'Both currently reconstructed by the hand sidecar `fixups/fourhorsemenmedia.com/webflow-tabs.js`, ' +
          'which AG-4 step 8 retires: the listing-media Social Ready / Cinema Grade switcher, and the ' +
          'FAQ, whose `.w-tabs` contains 26 accordion items and is therefore the nested-widget fixture.'
      },
      {
        id: 'aria',
        host: null,
        exclude: [],
        controlsScope: 'host',
        config: null,
        stateClasses: [],
        // DECLARED, NOT SHIPPED. `runtime: null` at the variant level keeps it
        // out of the stamp pass.
        runtime: null,
        evidence:
          '2026-09-07 census: `[role="tablist"]` = 30 sections / 13 sites (turnerpainting.com 8, ' +
          'designgandalf.com 6, benjisleep.com 3, breton-law.com 2, crossfitmazamet.fr 2, ' +
          'fourhorsemenmedia.com 2, and 7 sites with 1). FIFTEEN TIMES the Webflow population, and ' +
          'that is exactly why it is not stamped here: reconstructing 30 sections across 13 sites on ' +
          'the strength of a marker count, with no site exercised in a browser, is a corpus-wide ' +
          "behaviour change disguised as a release. It is the strongest candidate for AG-12's " +
          'promotion sprint, where admission requires fixtures, an ownership matrix and a ' +
          'rights-cleared holdout.'
      }
    ],
    evidence:
      'Tabs are the AG-4 release because ONE site carries the Webflow form and its behaviour is ' +
      'already reconstructed by hand, so the phase can prove the stamp against a known-good ' +
      'answer and then delete the hand copy.'
  },
  {
    id: 'carousel',
    // AG-6. Static on disk is not a gap: Motion Restoration collapses a captured
    // carousel to its first real slide with inert controls, which is a correct
    // resting state for a thumbnail and the wrong one for a visitor. The runtime
    // WAKES it — removes the collapse stamp so the site's own track CSS applies
    // again — and drives the slides itself. The wake is gated on
    // `html[data-pb-static-capture]` (locked decision 10): a saved preview or a
    // static capture renders exactly one slide, as the parser left it.
    support: 'runtime',
    runtime: 'carousel.js',
    // `fixups/fourhorsemenmedia.com/swiper.js` wrote this to claim `.swiper`;
    // AG-6 deletes that sidecar. The guard stays listed so a project saved with
    // the old sidecar js keeps ONE owner.
    legacyGuards: ['data-pb-slider'],
    // C-3, declared in AG-7. Ten selector literals below also appear in
    // `shared/motion-patterns.js` — `.swiper`, `.swiper-container`,
    // `.swiper-wrapper`, `.swiper-slide`, `.swiper-button-prev`,
    // `.swiper-button-next`, `.swiper-pagination`, `.splide`, `.splide__list`,
    // `.splide__slide`, `.splide__pagination`. That is not a hand copy, it is
    // the SAME widget seen by the two registries that between them own its whole
    // life: motion COLLAPSES it to a resting first slide on disk, and this
    // registry's runtime WAKES it. The residue gate refuses an undeclared
    // duplicate literal precisely so that a third copy — a selector pasted into
    // a table because it looked handy — cannot hide among these.
    sharedWith: ['motion-patterns#swiper', 'motion-patterns#splide'],
    // AG-14. What the wake changes against a crawl that froze the LIVE widget
    // at some slide, per role, and nothing else. A constrained fidelity claim
    // (`_builder-context-budget.json` schema 4, `transition: 'wake'`) may
    // explain exactly these differences, on elements inside a stamped host,
    // and the gate checks each relation — so a 100px offset on one slide, a
    // colour, a font or a lost element is never "the wake". Bullet colours and
    // pagination widths are deliberately NOT here: fourhorsemen's come from an
    // unfetched swiper-bundle.css and from the bullet count, not from resting
    // at slide 1. Roles resolve through the variant's own config selectors
    // (`claimRolesFor`), the same ones the runtime binds with.
    transitions: {
      wake: {
        effects: [
          {
            role: 'track',
            channel: 'computed',
            property: 'transform',
            relation: 'translate-x-only'
          },
          {
            role: 'track',
            channel: 'geometry',
            property: 'x',
            relation: 'uniform-shift',
            anchor: true
          },
          {
            role: 'slide',
            channel: 'geometry',
            property: 'x',
            relation: 'uniform-shift',
            descendants: true
          },
          { role: 'slide', channel: 'classes', property: 'class', relation: 'state-only' },
          { role: 'bullet', channel: 'classes', property: 'class', relation: 'state-only' },
          { role: 'control', channel: 'classes', property: 'class', relation: 'state-only' }
        ],
        evidence:
          'fourhorsemenmedia.com listing-media, measured 2026-09-16 from the row-unbudgeted ' +
          'transcript: the crawl froze custom-videos one slide in (track matrix tx -324) and the ' +
          'runtime rests at slide 1, so the track and every slide subtree sit +324 in x on all ten ' +
          'scopes and prev/active/next rotate by one slide. The testimonials carousel froze at ' +
          'slide 1 and has no wake delta at all; its bullet colours and pagination width are not ' +
          'wake effects and stay in the exact scopes.'
      }
    },
    variants: [
      {
        id: 'swiper',
        host: '.swiper, .swiper-container',
        exclude: [],
        controlsScope: 'ancestor',
        config: {
          track: '.swiper-wrapper',
          slide: '.swiper-slide',
          prev: '.swiper-button-prev',
          next: '.swiper-button-next',
          pagination: '.swiper-pagination',
          bullet: 'swiper-pagination-bullet',
          bulletActive: 'swiper-pagination-bullet-active',
          slideActive: 'swiper-slide-active',
          slidePrev: 'swiper-slide-prev',
          slideNext: 'swiper-slide-next',
          disabled: 'swiper-button-disabled',
          // Swiper writes `aria-controls="<track id>"` on the elements its
          // `navigation`/`pagination` options name, whatever their classes.
          // fourhorsemen's arrows are `a.arrow#third-left/#third-right`, not
          // `.swiper-button-*`, and the captured config says so. That relation
          // is the primary lookup; the class selectors above are the fallback.
          controlsByAria: true,
          ariaControlled: '[aria-controls]',
          staticAttr: 'data-pb-motion-static',
          inertAttr: 'data-pb-motion-inert',
          swipeThreshold: 40
        },
        stateClasses: [
          'swiper-slide-active',
          'swiper-slide-prev',
          'swiper-slide-next',
          'swiper-button-disabled',
          'swiper-pagination-bullet-active'
        ],
        evidence:
          '2026-09-08 index census: 25 sections / 13 sites (ecogardensolution.com 11, agileclaim.app 2, ' +
          'fourhorsemenmedia.com 2, catholic-andover.org.uk 1, gillrushdemo 1, kingsbarbercollege.com 1, ' +
          'madscientistdemo 1, philquinnellphotography.com 1, pomboscontracting.com 11 in a second ' +
          'shape, racnroll.com 1, thebeyondclean.com 1, trevimortgage.ca 1, valc-conseil.fr 1, ' +
          'villademazamet.com 1). The 2026-09-07 figure of 85 counted per-page variants and mirrors. ' +
          'AG-1 captured two REAL Swiper configurations from fourhorsemenmedia.com inline scripts — ' +
          '`slidesPerView: "auto"`, `spaceBetween: 16`, `navigation: {nextEl: "#third-right", prevEl: ' +
          '"#third-left"}`, `pagination: {el: "#swiper-pagination233", clickable: true}` — read ' +
          'without evaluating a line; the second one also carries `autoplay: {delay: 1000}`, which the ' +
          'runtime deliberately does NOT reproduce (policy: never autoplays). Only fourhorsemen can ' +
          'ship in AG-6: it is the one carousel site with a builder-context budget (C-7).'
      },
      {
        id: 'splide',
        host: '.splide',
        exclude: [],
        controlsScope: 'ancestor',
        config: {
          track: '.splide__list',
          slide: '.splide__slide',
          prev: '.splide__arrow--prev',
          next: '.splide__arrow--next',
          pagination: '.splide__pagination',
          bullet: 'splide__pagination__page',
          bulletActive: 'is-active',
          slideActive: 'is-active',
          slidePrev: 'is-prev',
          slideNext: 'is-next',
          disabled: 'is-disabled',
          controlsByAria: true,
          ariaControlled: '[aria-controls]',
          staticAttr: 'data-pb-motion-static',
          inertAttr: 'data-pb-motion-inert',
          swipeThreshold: 40
        },
        stateClasses: ['is-active', 'is-visible', 'is-prev', 'is-next', 'is-disabled'],
        evidence:
          '2026-09-08 index census: 49 sections / 3 sites (racnroll.com 47, grassrootsplumbing.com 1, ' +
          'youngagainrefinishing.com 1). racnroll.com is also the site whose splide sections keep ' +
          '`motion-residue-sweep` red on the standing baseline, and its 47 are Shopify Rebuy widgets ' +
          'whose `.splide__list` is EMPTY in the crawl (the products load client-side), so the ' +
          "runtime's `< 2 slides ⇒ no bind` rule is what keeps them inert. No splide site has a " +
          'builder-context budget; the variant ships on the synthetic fixture and is exercised in ' +
          '`behaviour-runtime-tests.js`.'
      }
    ],
    evidence:
      'The largest reconstructable population in the corpus after accordions, and the one with ' +
      'machine-readable configuration already captured.'
  },
  {
    id: 'accordion',
    support: 'runtime',
    runtime: 'accordion.js',
    legacyGuards: [],
    variants: [
      {
        id: 'aria',
        host: null,
        exclude: [],
        controlsScope: 'host',
        config: null,
        stateClasses: [],
        // DECLARED, NOT SHIPPED — the `tabs:aria` decision, re-measured.
        runtime: null,
        evidence:
          '2026-09-08 index census: `[aria-expanded][aria-controls]` = 666 sections / 95 sites, of ' +
          'which 280 are NAVS carrying `generateNavJS` (dropdowns), benjisleep.com alone has 344 and ' +
          'racnroll.com 179 (Shopify theme disclosures). The same attribute pair dresses a nav toggle, ' +
          'a disclosure, a dropdown and a real accordion, so a recogniser keyed on it alone would ' +
          'claim hundreds of elements nobody has opened in a browser. Not stamped; AG-12 admission.'
      },
      {
        id: 'details',
        host: 'details',
        exclude: [],
        controlsScope: 'host',
        config: null,
        stateClasses: [],
        // NATIVE: a <details> accordion opens itself. Supported without a runtime,
        // never stamped; proven in a browser by behaviour-browser-check.
        native: true,
        evidence:
          '2026-09-08 index census: 535 sections / 17 sites. Native `<details>` needs NO runtime — ' +
          'the browser owns it. The row exists so the residue gate can tell "already works" apart ' +
          'from "unclaimed", which is the distinction that decides whether a site gets a backlog entry.'
      },
      {
        id: 'elementor',
        host: '.elementor-accordion, .elementor-toggle',
        exclude: [],
        controlsScope: 'host',
        config: {
          item: '.elementor-accordion-item, .elementor-toggle-item',
          trigger: '.elementor-tab-title',
          panel: '.elementor-tab-content',
          activeClass: 'elementor-active',
          exclusive: true
        },
        stateClasses: ['elementor-active'],
        evidence:
          '2026-09-07 census: 15 sections / 1 site in per-page files; 2026-09-08 index census: 0 ' +
          'indexed sections. unmeasured — synthetic fixture (`behaviour-runtime-tests.js`); the ' +
          'residue gate (AG-7) is the tripwire for a real host.'
      },
      {
        id: 'avada',
        host: '.fusion-accordian, .panel-group',
        exclude: [],
        controlsScope: 'host',
        config: {
          item: '.panel',
          trigger: '.panel-title a, .panel-title button, [data-toggle="collapse"]',
          panel: '.panel-collapse',
          activeClass: 'in',
          collapsedClass: 'collapsed',
          exclusive: true
        },
        stateClasses: ['in', 'collapsed'],
        evidence:
          '2026-09-08 index census: 9 sections / 1 site (pomboscontracting.com, the Avada site AG-8 ' +
          're-ingests; no builder-context budget, so nothing there ships before AG-8). Exercised on ' +
          'the synthetic fixture in `behaviour-runtime-tests.js`.'
      }
    ],
    evidence:
      'AG-6 ships the runtime for the two platform shapes with a fixed markup contract. The ARIA ' +
      'shape is the largest population in the corpus and exactly why it stays declared.'
  },
  {
    id: 'mobile-nav',
    support: 'runtime',
    runtime: 'mobile-nav.js',
    legacyGuards: [],
    // Stamped by the parser's NAV-SHAPE pass (`shared/nav-shape.js`), never by
    // the host-selector pass: a nav is stamped only on a site whose platform
    // sidecar says `decisions.navShape === 'declared'`, and every legacy site
    // keeps the exact `generateNavJS` string (locked decision 12).
    variants: [
      {
        id: 'codestitch',
        host: null,
        exclude: [],
        controlsScope: 'ancestor',
        config: null,
        stateClasses: ['cs-active'],
        runtime: null,
        evidence:
          '2026-09-08 nav census: 317 of 397 navs. Locked decision 12: CodeStitch navs KEEP the ' +
          'generated string, byte-for-byte. This row exists to be excluded, not stamped.'
      },
      {
        id: 'webflow',
        host: null,
        stampedBy: 'nav-shape',
        exclude: [],
        controlsScope: 'host',
        config: {
          button: '.w-nav-button',
          overlay: '.w-nav-overlay',
          menu: '.w-nav-menu',
          openClass: 'w--open',
          menuOpenAttr: 'data-nav-menu-open',
          // Webflow's runtime MOVES the menu into the overlay on open and back on
          // close (`overlayMove`); the site CSS hides `.w-nav-menu` at the collapse
          // breakpoint and only styles it inside the overlay. Verified against the
          // fourhorsemen nav in `behaviour-fidelity-check.mjs` at 375px.
          overlayMove: true,
          // Webflow shows the overlay by inline display; the site's scoped
          // `.w-nav-overlay{display:none}` outranks the global `.w--open` by
          // source order, so the class alone never opens it (measured by
          // behaviour-fidelity-check on the first AG-6 run).
          overlayDisplay: 'block',
          escapeCloses: true,
          bodyLock: false
        },
        stateClasses: ['w--open'],
        evidence:
          '2026-09-08 nav census: 1 of 397 navs (fourhorsemenmedia.com). Its `.w-nav-overlay` is ' +
          '`display:none` and `.w--open{display:block}` in the site CSS; `.w-nav-button` is the only ' +
          'trigger, `aria-controls="w-nav-overlay-0"`, `aria-expanded="false"` frozen by the crawl. ' +
          'Today it carries only `generateNavJS`’s scroll handler — the menu button is DEAD.'
      },
      {
        id: 'hamburger-menu',
        host: null,
        stampedBy: 'nav-shape',
        exclude: [],
        controlsScope: 'ancestor',
        // generateNavJS's hamburger branch, as data: `.hamburger-menu` toggles
        // `clicked` on itself, `open` on `#navbar-menu` and on `<body>`, and
        // Escape closes. The legacy corpus keeps that generated string
        // (locked decision 12); a DECLARED site gets the same behaviour through
        // the runtime, so the two never disagree about what the shape means.
        config: {
          button: '.hamburger-menu, #hamburger-menu, .hamburger',
          menu: '#navbar-menu',
          menuVia: 'selector',
          link: 'a[href]',
          token: 'open',
          buttonToken: 'clicked',
          bodyToken: 'open',
          hiddenAttr: false,
          escapeCloses: true,
          bodyLock: false
        },
        stateClasses: ['open', 'clicked'],
        evidence:
          '2026-09-08 nav census: 9 navs carry `.hamburger-menu` + `#navbar-menu` and are served by ' +
          "generateNavJS's hamburger branch; 20 more carry a `.hamburger` with no `#navbar-menu` and " +
          "get only its scroll handler. 2026-09-16 ingest census: 8 of 383 strangers' navs. " +
          "Admitted in AG-12: the config is the generated string's own convention."
      },
      {
        id: 'aria-toggle',
        host: null,
        stampedBy: 'nav-shape',
        exclude: [],
        controlsScope: 'host',
        // ATTRIBUTE-DRIVEN. The runtime toggles `aria-expanded` on the button,
        // `hidden` / `aria-hidden` on the menu, and no class at all — for a site
        // whose CSS keys the menu on `[aria-expanded="true"]` or `[hidden]`.
        // The nav-shape pass places this variant only when the CSS does
        // (`cssStylesAttributeState`) or the menu is `hidden` at rest.
        config: {
          button:
            'button[aria-expanded][aria-controls], [role="button"][aria-expanded][aria-controls]',
          menuVia: 'aria-controls',
          link: 'a[href]',
          token: null,
          hiddenAttr: true,
          escapeCloses: true,
          bodyLock: false
        },
        stateClasses: [],
        evidence:
          '2026-09-16 ingest census: 50 of 383 navs answer the aria rung, of which an unmeasured ' +
          'share are dropdown toggles (`menuToggleOf` now requires the controlled element to hold ' +
          'the links). The corpus has no attribute-keyed instance: unmeasured — synthetic fixture.'
      },
      {
        id: 'aria-toggle-open',
        host: null,
        stampedBy: 'nav-shape',
        exclude: [],
        controlsScope: 'host',
        config: {
          button:
            'button[aria-expanded][aria-controls], [role="button"][aria-expanded][aria-controls]',
          menuVia: 'aria-controls',
          link: 'a[href]',
          token: 'open',
          hiddenAttr: true,
          escapeCloses: true,
          bodyLock: false
        },
        stateClasses: ['open'],
        evidence:
          '2026-09-08 nav census, re-read in AG-12: tcvisualsllc.com — `.menu-toggle` controls ' +
          '`#primary-navigation` and the site CSS ' +
          'opens it with `.main-nav.open{opacity:1;visibility:visible;max-height:350px}`, a rule ' +
          'css-prune kept in `_globals.css`. Chosen by `detectMenuOpenToken` because the token is ' +
          'styled ON the menu; `.main-nav a.active` on the same site is the decoy it refuses.'
      },
      {
        id: 'aria-toggle-active',
        host: null,
        stampedBy: 'nav-shape',
        exclude: [],
        controlsScope: 'host',
        config: {
          button:
            'button[aria-expanded][aria-controls], [role="button"][aria-expanded][aria-controls]',
          menuVia: 'aria-controls',
          link: 'a[href]',
          token: 'active',
          hiddenAttr: true,
          escapeCloses: true,
          bodyLock: false
        },
        stateClasses: ['active'],
        evidence:
          '2026-09-16 ingest census: `active` is a dormant state class on 35 of 383 hosts, the ' +
          'commonest after `hidden`. No corpus nav opens its menu with it (the two that style it ' +
          'style a link and a dropdown): unmeasured against a nav — synthetic fixture.'
      },
      {
        id: 'aria-toggle-is-open',
        host: null,
        stampedBy: 'nav-shape',
        exclude: [],
        controlsScope: 'host',
        config: {
          button:
            'button[aria-expanded][aria-controls], [role="button"][aria-expanded][aria-controls]',
          menuVia: 'aria-controls',
          link: 'a[href]',
          token: 'is-open',
          hiddenAttr: true,
          escapeCloses: true,
          bodyLock: false
        },
        stateClasses: ['is-open'],
        evidence:
          '2026-09-16 ingest census: 0 navs measured with it. unmeasured — synthetic fixture.'
      },
      {
        id: 'aria-toggle-is-active',
        host: null,
        stampedBy: 'nav-shape',
        exclude: [],
        controlsScope: 'host',
        config: {
          button:
            'button[aria-expanded][aria-controls], [role="button"][aria-expanded][aria-controls]',
          menuVia: 'aria-controls',
          link: 'a[href]',
          token: 'is-active',
          hiddenAttr: true,
          escapeCloses: true,
          bodyLock: false
        },
        stateClasses: ['is-active'],
        evidence:
          '2026-09-16 ingest census: `is-active` is a dormant state class on 20 of 383 hosts. ' +
          'unmeasured against a nav — synthetic fixture.'
      },
      {
        id: 'elementor',
        host: null,
        exclude: [],
        controlsScope: 'ancestor',
        config: null,
        stateClasses: ['elementor-active'],
        runtime: null,
        evidence: '2026-09-08 nav census: 1 nav (thebeyondclean.com). Declared; AG-8 re-ingests it.'
      },
      {
        id: 'details',
        host: null,
        stampedBy: 'nav-shape',
        exclude: [],
        controlsScope: 'host',
        config: null,
        stateClasses: [],
        // NATIVE: `<details>` opens itself. Counted as supported, never stamped
        // (there is nothing to bind), and proven in a browser by
        // behaviour-browser-check rather than assumed.
        native: true,
        evidence:
          '2026-09-08 nav census: 2 navs (montagnenoirewebstudio.com, picosainkbeautybar.com). ' +
          '2026-09-16 ingest census: 13 of 383 navs (11 of them Shopify menu drawers). Native.'
      }
    ],
    evidence:
      'AG-6, with the nav-shape ladder. One shape ships (webflow) because one declared site ' +
      'carries it and its fixture can be driven in a browser.'
  },
  {
    id: 'nav-scroll',
    support: 'runtime',
    runtime: 'nav-scroll.js',
    legacyGuards: [],
    // Stamped by the nav-shape pass, and ONLY when the site's own CSS styles the
    // token (`detectScrollStateClass`): a scrolled-state class no rule matches
    // would be a runtime doing work nobody can see.
    variants: [
      {
        id: 'scroll',
        host: null,
        stampedBy: 'nav-shape',
        exclude: [],
        controlsScope: 'host',
        config: { token: 'scroll', onBody: true, onHost: true, threshold: 100 },
        stateClasses: ['scroll'],
        evidence:
          '2026-09-08 nav census: 160 of 397 navs style `body.scroll` — the CodeStitch convention ' +
          "generateNavJS's scroll handler serves on every legacy nav. A declared site whose CSS " +
          'styles it gets this stamp instead of the generated string.'
      },
      {
        id: 'scrolled',
        host: null,
        stampedBy: 'nav-shape',
        exclude: [],
        controlsScope: 'host',
        config: { token: 'scrolled', onBody: true, onHost: true, threshold: 100 },
        stateClasses: ['scrolled'],
        evidence: '2026-09-08 nav census: 1 of 397 navs styles `.scrolled`.'
      },
      {
        id: 'is-scrolled',
        host: null,
        stampedBy: 'nav-shape',
        exclude: [],
        controlsScope: 'host',
        config: { token: 'is-scrolled', onBody: true, onHost: true, threshold: 100 },
        stateClasses: ['is-scrolled'],
        evidence: '2026-09-08 nav census: 0 of 397 navs. unmeasured — synthetic fixture.'
      },
      {
        id: 'sticky',
        host: null,
        stampedBy: 'nav-shape',
        exclude: [],
        controlsScope: 'host',
        config: { token: 'sticky', onBody: true, onHost: true, threshold: 100 },
        stateClasses: ['sticky'],
        evidence: '2026-09-08 nav census: 0 of 397 navs. unmeasured — synthetic fixture.'
      }
    ],
    evidence:
      'The scrolled-state class is the one behaviour EVERY legacy nav already gets, unconditionally, ' +
      'from `generateNavJS` — including the 236 navs whose CSS never styles it. A declared site gets ' +
      'it only when it means something.'
  },
  {
    id: 'smooth-scroll',
    support: 'none',
    runtime: null,
    legacyGuards: [],
    variants: [],
    evidence:
      'Reserved. Modern browsers do this with `scroll-behavior: smooth`, so the interesting case is ' +
      'a site whose captured script did it in JS AND whose CSS does not — unmeasured, and the ' +
      'census (AG-9+) is the tripwire.'
  }
];

/**
 * Interactive libraries we can recognise and do not reconstruct. Present so a
 * crawl that introduces one is REPORTED rather than shipped as frozen markup.
 *
 * Note what the census found, because motion-patterns' equivalent list still
 * says "None of these currently appear in the corpus" and that is no longer
 * true: `.slick-slider` is in the BUILT corpus today — benjisleep.com (9
 * sections) and racnroll.com (1). AG-7's rule is that an unsupported marker in
 * the built corpus FAILS, so those 10 sections are a real decision that phase
 * has to make, not a hypothetical.
 */
export const UNSUPPORTED_BEHAVIOUR_MARKERS = [
  {
    id: 'webflow-ix2',
    markers: ['[data-w-id]'],
    evidence: '2026-09-07 census: 6 sections / 1 site (fourhorsemenmedia.com).',
    reason:
      'IX2 is an interpreter for a choreography format, not a widget. Named out of scope by the ' +
      'plan; the fixups that stand in for it cite it as their `residueOf`.'
  },
  {
    id: 'slick',
    markers: ['.slick-slider', '.slick-track', '.slick-slide'],
    evidence: '2026-09-07 census: 10 sections / 2 sites (benjisleep.com 9, racnroll.com 1).',
    reason:
      'No runtime. Recorded with a real count because AG-7 fails on an unsupported marker in the ' +
      'built corpus, and this is the row that will fire.'
  },
  {
    id: 'flickity',
    markers: ['.flickity-enabled', '[data-flickity]'],
    evidence: '2026-09-07 census: 1 section / 1 site (findleads.app).',
    reason: 'No runtime.'
  },
  {
    id: 'scroll-stage',
    markers: [],
    evidence: 'unmeasured — synthetic fixture. Tripwire: `behaviour-residue-sweep.js` (AG-7).',
    reason:
      'A scroll-scrubbed stage is a timeline, not a widget: it has no resting state to reconstruct ' +
      'toward. Named out of scope by the plan.'
  },
  {
    id: 'hover-swap',
    markers: [],
    evidence: 'unmeasured — synthetic fixture. Tripwire: `behaviour-residue-sweep.js` (AG-7).',
    reason: 'Out of scope by the plan; the fourhorsemen hover-icons CSS fixup cites it.'
  },
  {
    id: 'counter',
    markers: ['[data-count]', '[data-counter]', '.counter'],
    evidence: '2026-09-07 census: 56 sections / 6 sites.',
    reason:
      'Count-up counters are named out of scope by the plan. The markers are also weak — `.counter` ' +
      'dresses plenty of static markup — which is a second reason not to claim them.'
  }
];

/**
 * Behaviour residue that is real and deliberately left in place, each with the
 * reason it cannot be improved. AG-7 treats these as known, not as violations,
 * and `postparse-template-fixups.js` requires every `setJs`/`prependCss` op to
 * name one (or `'not-behaviour'`) in its `residueOf`.
 */
export const BEHAVIOUR_KNOWN_GAPS = [
  {
    id: 'webflow-ix2',
    reason:
      'The IX2 interpreter is out of scope. A site whose accordion or scroll animation was driven ' +
      'by webflow.js keeps a hand sidecar, and that sidecar cites this id.'
  },
  {
    id: 'scroll-stage',
    reason: 'A 500vh scroll-pinned stage has no resting state to reconstruct toward.'
  },
  {
    id: 'hover-swap',
    reason: 'Pointer-driven icon swaps are out of scope by the plan.'
  },
  {
    id: 'counter',
    reason: 'Count-up counters are out of scope by the plan.'
  },
  {
    id: 'custom-nav',
    reason:
      'A nav whose behaviour came from a site-specific external bundle the crawl never fetched, and ' +
      'which no registry variant describes (herocaredemo.netlify.app).'
  }
];
